/**
 * PrototypePollutionDetector — Detection of prototype pollution attempt indicators
 * in client-side outbound requests.
 *
 * Prototype pollution injects properties into Object.prototype or Function.prototype
 * through user-controlled keys that reach Object.assign(), lodash.merge(), or similar
 * deep-merge functions. This can override default object properties globally,
 * enabling privilege escalation, DoS, or property injection attacks.
 *
 * Attack vectors detected:
 *   1. __proto__ in URL query parameter names: ?__proto__[admin]=true
 *   2. constructor[prototype] path in URL params: ?constructor[prototype][admin]=true
 *   3. URL-encoded __proto__ (%5f%5fproto%5f%5f) — filter bypass attempt
 *   4. __proto__ as a key in JSON request body: {"__proto__": {"admin": true}}
 *   5. constructor.prototype path in JSON body: {"constructor": {"prototype": {...}}}
 *
 * CWE-1321 | OWASP A08:2025 – Software and Data Integrity Failures
 */

/** Literal __proto__ — matches in param names, JSON keys, or raw strings. */
const PROTO_LITERAL = /__proto__/;

/**
 * URL-encoded form of __proto__: %5f = _, so %5f%5fproto%5f%5f = __proto__.
 * Encoding the underscores is a deliberate bypass of string-based filters.
 */
const ENCODED_PROTO = /%5f%5fproto%5f%5f/i;

/**
 * constructor.prototype or constructor[prototype] — alternative pollution path
 * that does not require __proto__ directly but achieves the same effect by
 * navigating the constructor chain.
 */
const CONSTRUCTOR_PROTO = /constructor\s*[\.\[]\s*prototype/i;

/**
 * Object.prototype.<property> assignment in a request string value.
 * Detects JavaScript code targeting the global prototype directly rather than
 * through bracket notation. Example: Object.prototype.isAdmin = true
 */
const OBJECT_PROTO_ASSIGN = /\bObject\s*\.\s*prototype\s*\.\s*\w+/i;

/**
 * .constructor.prototype.<property> chain in a request string value.
 * Indirect traversal: obj.constructor.prototype.x = y — achieves prototype
 * write without using __proto__ and bypasses filters that only block __proto__.
 */
const CONSTRUCTOR_PROTO_CHAIN = /\.\s*constructor\s*\.\s*prototype\s*\.\s*\w+/i;

export class PrototypePollutionDetector {
	/**
	 * Analyzes an outbound request for prototype pollution indicators.
	 *
	 * @param {{ url: string, body?: string }} options
	 * @returns {{ detected: true,  type: 'PROTOTYPE_POLLUTION', variant: string,
	 *             evidence: string, confidence: 'high' }
	 *          | { detected: false }}
	 */
	analyze({ url, body }) {
		if (url && typeof url === 'string') {
			const urlHit = this._scanUrl(url);
			if (urlHit) return urlHit;
		}

		if (body && typeof body === 'string' && body.length > 0) {
			const bodyHit = this._scanBody(body);
			if (bodyHit) return bodyHit;
		}

		return { detected: false };
	}

	/**
	 * Scans the URL query string for prototype pollution indicators.
	 * Checks the raw string first (for encoded forms), then decoded param names.
	 *
	 * @param {string} url
	 * @returns {object|null}
	 */
	_scanUrl(url) {
		let rawSearch;
		try {
			rawSearch = new URL(url).search;
		} catch (_) {
			const idx = url.indexOf('?');
			if (idx === -1) return null;
			rawSearch = url.substring(idx);
		}
		if (!rawSearch || rawSearch === '?') return null;

		// 1. URL-encoded __proto__ in raw query string — high confidence bypass attempt
		if (ENCODED_PROTO.test(rawSearch)) {
			const match = rawSearch.match(ENCODED_PROTO);
			return {
				detected: true,
				type: 'PROTOTYPE_POLLUTION',
				variant: 'encoded_proto',
				evidence: match[0],
				confidence: 'high'
			};
		}

		// 2. Literal __proto__ in raw query string (before URL decoding)
		if (PROTO_LITERAL.test(rawSearch)) {
			const match = rawSearch.match(PROTO_LITERAL);
			return {
				detected: true,
				type: 'PROTOTYPE_POLLUTION',
				variant: 'proto_param',
				evidence: match[0],
				confidence: 'high'
			};
		}

		// 3. constructor[prototype] or constructor.prototype in raw query string
		if (CONSTRUCTOR_PROTO.test(rawSearch)) {
			const match = rawSearch.match(CONSTRUCTOR_PROTO);
			return {
				detected: true,
				type: 'PROTOTYPE_POLLUTION',
				variant: 'constructor_param',
				evidence: match[0],
				confidence: 'high'
			};
		}

		// 4. Object.prototype / constructor.prototype assignment in raw query string value
		if (OBJECT_PROTO_ASSIGN.test(rawSearch)) {
			const match = rawSearch.match(OBJECT_PROTO_ASSIGN);
			return {
				detected: true,
				type: 'PROTOTYPE_POLLUTION',
				variant: 'prototype_dot_notation',
				evidence: match[0].substring(0, 64),
				confidence: 'high'
			};
		}
		if (CONSTRUCTOR_PROTO_CHAIN.test(rawSearch)) {
			const match = rawSearch.match(CONSTRUCTOR_PROTO_CHAIN);
			return {
				detected: true,
				type: 'PROTOTYPE_POLLUTION',
				variant: 'constructor_prototype_chain',
				evidence: match[0].substring(0, 64),
				confidence: 'high'
			};
		}

		// 5. Check decoded param names (catches percent-encoded variants other than %5f)
		let params;
		try {
			params = new URLSearchParams(rawSearch);
		} catch (_) {
			return null;
		}

		for (const [name] of params.entries()) {
			if (PROTO_LITERAL.test(name)) {
				return {
					detected: true,
					type: 'PROTOTYPE_POLLUTION',
					variant: 'proto_param',
					evidence: name.substring(0, 64),
					confidence: 'high'
				};
			}
			if (CONSTRUCTOR_PROTO.test(name)) {
				return {
					detected: true,
					type: 'PROTOTYPE_POLLUTION',
					variant: 'constructor_param',
					evidence: name.substring(0, 64),
					confidence: 'high'
				};
			}
		}

		return null;
	}

	/**
	 * Scans a request body for prototype pollution indicators.
	 * Handles JSON bodies (deep key scan) and URL-encoded bodies (raw pattern scan).
	 *
	 * @param {string} body
	 * @returns {object|null}
	 */
	_scanBody(body) {
		const firstChar = body.charAt(0);

		// JSON body: parse and scan all keys recursively
		if (firstChar === '{' || firstChar === '[') {
			try {
				const parsed = JSON.parse(body);
				if (typeof parsed === 'object' && parsed !== null) {
					return this._scanJsonObject(parsed, 0);
				}
			} catch (_) {
				// Not valid JSON — fall through to raw scan below
			}
		}

		// URL-encoded body or non-JSON string: raw pattern scan
		if (ENCODED_PROTO.test(body)) {
			const match = body.match(ENCODED_PROTO);
			return {
				detected: true,
				type: 'PROTOTYPE_POLLUTION',
				variant: 'encoded_proto',
				evidence: match[0],
				confidence: 'high'
			};
		}

		if (PROTO_LITERAL.test(body)) {
			const match = body.match(PROTO_LITERAL);
			return {
				detected: true,
				type: 'PROTOTYPE_POLLUTION',
				variant: 'proto_param',
				evidence: match[0],
				confidence: 'high'
			};
		}

		if (CONSTRUCTOR_PROTO.test(body)) {
			const match = body.match(CONSTRUCTOR_PROTO);
			return {
				detected: true,
				type: 'PROTOTYPE_POLLUTION',
				variant: 'constructor_param',
				evidence: match[0],
				confidence: 'high'
			};
		}

		// Object.prototype / constructor.prototype assignment patterns in body string values
		if (OBJECT_PROTO_ASSIGN.test(body)) {
			const match = body.match(OBJECT_PROTO_ASSIGN);
			return {
				detected: true,
				type: 'PROTOTYPE_POLLUTION',
				variant: 'prototype_dot_notation',
				evidence: match[0].substring(0, 64),
				confidence: 'high'
			};
		}
		if (CONSTRUCTOR_PROTO_CHAIN.test(body)) {
			const match = body.match(CONSTRUCTOR_PROTO_CHAIN);
			return {
				detected: true,
				type: 'PROTOTYPE_POLLUTION',
				variant: 'constructor_prototype_chain',
				evidence: match[0].substring(0, 64),
				confidence: 'high'
			};
		}

		return null;
	}

	/**
	 * Recursively scans a parsed JSON object for prototype pollution keys.
	 * Capped at depth 5 to prevent performance issues on deeply nested payloads.
	 *
	 * @param {object|Array} obj
	 * @param {number} depth
	 * @returns {object|null}
	 */
	_scanJsonObject(obj, depth) {
		if (depth > 5 || typeof obj !== 'object' || obj === null) return null;

		const entries = Array.isArray(obj)
			? obj.map((v, i) => [String(i), v])
			: Object.entries(obj);

		for (const [key, value] of entries) {
			// __proto__ as a JSON key — direct prototype chain write
			if (PROTO_LITERAL.test(key)) {
				return {
					detected: true,
					type: 'PROTOTYPE_POLLUTION',
					variant: 'proto_json_key',
					evidence: key.substring(0, 64),
					confidence: 'high'
				};
			}

			// constructor key with a prototype sub-key — indirect pollution path
			if (key === 'constructor' && typeof value === 'object' && value !== null && 'prototype' in value) {
				return {
					detected: true,
					type: 'PROTOTYPE_POLLUTION',
					variant: 'constructor_json_key',
					evidence: 'constructor.prototype',
					confidence: 'high'
				};
			}

			// Recurse into nested objects/arrays
			if (typeof value === 'object' && value !== null) {
				const nested = this._scanJsonObject(value, depth + 1);
				if (nested) return nested;
			}
		}

		return null;
	}
}

export default PrototypePollutionDetector;
